import { CSSProperties, useId } from 'react';

/**
 * CountStepper — a − / + counter on a hairline row, e.g. "How many of you ·
 * including you". Not to be confused with core/Stepper (a progress list).
 *
 * The value sits in a polite live region so each change is announced; the
 * buttons carry spoken labels since − and + alone say nothing.
 */
export interface CountStepperProps {
  /** Row label. */
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Spoken label for −. Default "One fewer". */
  decrementLabel?: string;
  /** Spoken label for +. Default "One more". */
  incrementLabel?: string;
  style?: CSSProperties;
}

export function CountStepper({
  label,
  value,
  onChange,
  min = 0,
  max = Infinity,
  decrementLabel = 'One fewer',
  incrementLabel = 'One more',
  style,
}: CountStepperProps) {
  const labelId = useId();
  const atMin = value <= min;
  const atMax = value >= max;

  const button = (disabled: boolean): CSSProperties => ({
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: 'none',
    background: 'var(--chip-bg)',
    // Disabled fades by token, never opacity.
    color: disabled ? 'var(--line)' : 'var(--text)',
    fontFamily: 'var(--font-sans)',
    fontSize: 20,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 56,
        borderTop: '1px solid var(--line)',
        borderBottom: '1px solid var(--line)',
        ...style,
      }}
    >
      <span id={labelId} style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--chip-text)' }}>
        {label}
      </span>
      <button type="button" onClick={() => onChange(value - 1)} disabled={atMin} aria-label={decrementLabel} className="sk-flow-focus" style={button(atMin)}>
        −
      </button>
      <span
        aria-live="polite"
        style={{
          minWidth: 28,
          textAlign: 'center',
          fontFamily: 'var(--font-serif)',
          fontSize: 19,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--text)',
        }}
      >
        {value}
      </span>
      <button type="button" onClick={() => onChange(value + 1)} disabled={atMax} aria-label={incrementLabel} className="sk-flow-focus" style={button(atMax)}>
        +
      </button>
    </div>
  );
}

export default CountStepper;
